import React, { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import countries from "@/constants/array/country_city.json";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";
import { MerchantApi } from "@/api";
import { toast } from "sonner";
import { Loader2, Trash, Upload } from "lucide-react";
import { IMerchant } from "@/interfaces/merchant.interfaces";

const schema = Yup.object().shape({
  businessName: Yup.string().required("Merchant Name is required"),
  merchantCode: Yup.string().required("Merchant code is required."),
  tier: Yup.string()
    .oneOf(
      [
        "WESTAY Bronze",
        "WESTAY Silver",
        "WESTAY Gold",
        "WESTAY Platinum",
        "WESTAY Prive",
      ],
      "Select a valid tier"
    )
    .required("Tier is required"),
  businessLocation: Yup.string(),
  businessDescription: Yup.string().required(
    "Merchant description is required"
  ),
  websiteUrl: Yup.string()
    .required("Website URL is required")
    .url("Website URL must be a valid URL format"),
  country: Yup.string().required("Country is required"),
  city: Yup.string().required("City is required"),
  categoryVOList: Yup.array()
    .of(
      Yup.object().shape({
        serverCategoryId: Yup.number().nullable().optional(),
        categoryCode: Yup.string(),
        categoryName: Yup.string().required("Category name is required"),
        cashbackPercentage: Yup.number()
          .typeError("Cashback percentage should be a number")
          .required("Cashback percentage is required")
          .positive("Cashback percentage must be greater than 0")
          .min(0.01, "Cashback percentage must be greater than 0")
          .max(100, "Cashback percentage cannot exceed 100%"),
      })
    )
    .min(1, "At least one category is required"),
});

type FormValues = Yup.InferType<typeof schema>;

const TIER_OPTIONS = [
  "WESTAY Bronze",
  "WESTAY Silver",
  "WESTAY Gold",
  "WESTAY Platinum",
  "WESTAY Prive",
] as const;

function normalizeTier(value: unknown): string {
  if (typeof value !== "string") return "";
  const match = TIER_OPTIONS.find(
    (opt) => opt.toLowerCase() === value.trim().toLowerCase()
  );
  return match || "";
}

function findCountryJsonByName(name: string | undefined) {
  if (!name) return "";
  const c = (countries as { name: string; cities?: string[] }[]).find(
    (x) => x.name === name
  );
  return c ? JSON.stringify(c) : "";
}

function buildDefaultValues(merchant: IMerchant & Record<string, unknown>): FormValues {
  const cats = (merchant as any)?.categoryVOList;
  const categoryVOList = Array.isArray(cats)
    ? cats.map((cat: any) => ({
        serverCategoryId: cat.id != null ? Number(cat.id) : undefined,
        categoryCode: cat.categoryCode || "",
        categoryName: cat.categoryName || "",
        cashbackPercentage:
          cat.cashbackPercentage != null
            ? Number(cat.cashbackPercentage)
            : 0.01,
      }))
    : [
        {
          serverCategoryId: undefined,
          categoryCode: "",
          categoryName: "",
          cashbackPercentage: 0.01,
        },
      ];

  return {
    businessName: merchant.merchantName || "",
    merchantCode: merchant.merchantCode || "",
    tier: normalizeTier(merchant.tier),
    businessLocation: merchant.businessLocation || "",
    businessDescription: merchant.businessDescription || "",
    websiteUrl: merchant.websiteUrl || "",
    country: findCountryJsonByName(merchant.country as string) || "",
    city: merchant.city || "",
    categoryVOList,
  };
}

export type EditOfferMerchantSheetProps = {
  merchant: IMerchant & Record<string, unknown>;
  merchantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (patch: Partial<IMerchant> & Record<string, unknown>) => void;
};

export const EditOfferMerchantSheet: React.FC<EditOfferMerchantSheetProps> = ({
  merchant,
  merchantId,
  open,
  onOpenChange,
  onUpdated,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [merchantLogo, setMerchantLogo] = useState<File | undefined>();
  const [selectedCountry, setSelectedCountry] = useState<{
    name: string;
    cities?: string[];
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>(
    merchant.merchantImageUri
  );

  useEffect(() => {
    if (merchantLogo) {
      const u = URL.createObjectURL(merchantLogo);
      setLogoPreviewUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setLogoPreviewUrl(merchant.merchantImageUri);
    return undefined;
  }, [merchantLogo, merchant.merchantImageUri]);

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    defaultValues: buildDefaultValues(merchant),
  });

  const {
    fields: categoryFields,
    append: appendCategory,
    remove: removeCategory,
  } = useFieldArray({
    control: form.control,
    name: "categoryVOList",
  });

  useEffect(() => {
    if (!open) {
      setMerchantLogo(undefined);
      return;
    }
    const defaults = buildDefaultValues(merchant);
    form.reset(defaults);
    try {
      if (defaults.country) {
        setSelectedCountry(JSON.parse(defaults.country));
      } else {
        setSelectedCountry(null);
      }
    } catch {
      setSelectedCountry(null);
    }
  }, [open, merchant, form]);

  const handleBrandLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) setMerchantLogo(selectedFile);
  };

  const handleSubmit = async (formData: FormValues) => {
    setSubmitting(true);
    try {
      const selectedCountryName =
        selectedCountry?.name ||
        (() => {
          try {
            const parsed = formData.country
              ? JSON.parse(formData.country)
              : null;
            return parsed?.name || "";
          } catch {
            return "";
          }
        })();

      const offerMerchantPayload: Record<string, unknown> = {
        id: Number(merchantId),
        merchantName: formData.businessName,
        merchantCode: formData.merchantCode,
        tier: formData.tier,
        businessDescription: formData.businessDescription,
        websiteUrl: formData.websiteUrl,
        city: formData.city,
        country: selectedCountryName,
        businessLocation: formData.businessLocation,
        categoryVOList: (formData.categoryVOList || []).map((category) => ({
          ...(category.serverCategoryId != null &&
          !Number.isNaN(Number(category.serverCategoryId))
            ? { id: Number(category.serverCategoryId) }
            : {}),
          cashbackPercentage: Number(category?.cashbackPercentage || 0),
          categoryName: category?.categoryName,
          categoryCode: category?.categoryCode || category?.categoryName,
        })),
      };

      const merchantData = new FormData();
      if (merchantLogo) {
        merchantData.append("merchantLogo", merchantLogo);
      }
      merchantData.append(
        "offeredMerchantVO",
        new Blob([JSON.stringify(offerMerchantPayload)], {
          type: "application/json",
        })
      );

      const res = await MerchantApi.createOrUpdateMerchantOfOfferPage(
        merchantData
      );

      const newImageUri =
        (res as any)?.data?.merchantImageUri ??
        (res as any)?.data?.merchantImageURI;

      toast.success("Merchant updated successfully");
      onOpenChange(false);
      setMerchantLogo(undefined);

      onUpdated({
        merchantName: formData.businessName,
        merchantCode: formData.merchantCode,
        tier: formData.tier,
        businessDescription: formData.businessDescription,
        websiteUrl: formData.websiteUrl,
        city: formData.city,
        country: selectedCountryName,
        businessLocation: formData.businessLocation,
        categoryVOList: (formData.categoryVOList || []).map((c) => ({
          ...(c.serverCategoryId != null
            ? { id: c.serverCategoryId }
            : {}),
          categoryCode: c.categoryCode,
          categoryName: c.categoryName,
          cashbackPercentage: c.cashbackPercentage,
        })),
        ...(newImageUri ? { merchantImageUri: newImageUri } : {}),
      });
    } catch (error: any) {
      toast.error(error?.message || "Failed to update merchant");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg md:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Merchant</SheetTitle>
          <SheetDescription>
            Update merchant details. Changes are saved to the Offer Point merchant
            profile.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-6 py-6"
          >
            <Card className="border border-gray-100 shadow-sm">
              <CardHeader className="card-header-compact bg-gray-50 py-3">
                <CardTitle className="text-base font-medium">
                  General information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-2">
                  <FormLabel>Merchant logo</FormLabel>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleBrandLogoChange}
                  />
                  <div
                    className="w-24 h-24 bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {logoPreviewUrl ? (
                      <img
                        src={logoPreviewUrl}
                        alt=""
                        className="max-h-20 max-w-20 object-contain rounded"
                      />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1">Upload</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Leave unchanged to keep the current logo. Optional on update.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="businessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant name*</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="merchantCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant code*</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tier*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select tier" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIER_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="businessDescription"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Description*</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          className={`min-h-[100px] ${
                            fieldState.error ? "border-red-500" : ""
                          }`}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Website URL*</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={fieldState.error ? "border-red-500" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country*</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            const selected = JSON.parse(value);
                            setSelectedCountry(selected);
                            field.onChange(value);
                            form.setValue("city", "");
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(countries as any)?.map(
                              (country: { name: string }) => (
                                <SelectItem
                                  key={country.name}
                                  value={JSON.stringify(country)}
                                >
                                  {country.name}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City*</FormLabel>
                        <FormControl>
                          <VirtualizedCitySelect
                            cities={selectedCountry?.cities || []}
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder="Select city"
                            disabled={!selectedCountry?.cities?.length}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="businessLocation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business location</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Card className="border border-gray-100 shadow-sm">
              <CardHeader className="card-header-compact bg-gray-50 py-3">
                <CardTitle className="text-base font-medium">Categories</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {categoryFields.map((field, index) => (
                  <div key={field.id} className="flex items-end gap-3 flex-wrap">
                    <FormField
                      control={form.control}
                      name={`categoryVOList.${index}.serverCategoryId`}
                      render={({ field: f }) => (
                        <input type="hidden" {...f} />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`categoryVOList.${index}.categoryName`}
                      render={({ field: f }) => (
                        <FormItem className="flex-1 min-w-[140px]">
                          <FormLabel>Category name</FormLabel>
                          <FormControl>
                            <Input {...f} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`categoryVOList.${index}.cashbackPercentage`}
                      render={({ field: f, fieldState }) => (
                        <FormItem className="w-28">
                          <FormLabel>Cashback %</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={0.01}
                              max={100}
                              step={0.01}
                              onWheel={(e) => e.currentTarget.blur()}
                              className={
                                fieldState.error ? "border-red-500" : ""
                              }
                              {...f}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-600 shrink-0"
                      onClick={() => removeCategory(index)}
                      disabled={categoryFields.length === 1}
                      aria-label="Remove category"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    appendCategory({
                      serverCategoryId: undefined,
                      categoryCode: "",
                      categoryName: "",
                      cashbackPercentage: 0.01,
                    })
                  }
                >
                  Add category
                </Button>
              </CardContent>
            </Card>

            <SheetFooter className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-rose-600 hover:bg-rose-700"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
